import { useState, FormEvent, useEffect } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleType: '',
    budget: '',
    timeline: '',
    buyingStage: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4tioqqtj9hepa8fj4rg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          vehicleType: '',
          budget: '',
          timeline: '',
          buyingStage: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold mb-4">Start Your AI Consultation</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Tell us what you're looking for and we'll get you the best deal in Ontario. Takes just 2 minutes.
          </p>
        </div>

        {/* Calendly Booking Section */}
        <div className="mb-16">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
            <div className="text-center mb-6">
              <h3 className="text-3xl font-bold mb-3">Book Your Appointment</h3>
              <p className="text-lg text-gray-300">
                Schedule a personalized consultation at your convenience
              </p>
            </div>
            <div 
              className="calendly-inline-widget rounded-2xl overflow-hidden" 
              data-url="https://calendly.com/kevinschleifer/appointment" 
              style={{ minWidth: '320px', height: '700px' }}
            ></div>
          </div>
        </div>

        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-4 text-gray-400">
            <div className="h-px bg-gray-600 w-24"></div>
            <span className="text-lg font-semibold">OR</span>
            <div className="h-px bg-gray-600 w-24"></div>
          </div>
          <p className="mt-4 text-gray-300">Fill out the quick form below</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* Contact Form */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
            <form id="ai-consultation-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="John Smith"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="(416) 555-0123"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">What type of vehicle are you looking for? *</label>
                <select
                  name="vehicleType"
                  required
                  value={formData.vehicleType}
                  onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 cursor-pointer"
                >
                  <option value="" className="bg-gray-800">Select vehicle type</option>
                  <option value="SUV" className="bg-gray-800">SUV</option>
                  <option value="Sedan" className="bg-gray-800">Sedan</option>
                  <option value="Truck" className="bg-gray-800">Truck</option>
                  <option value="Coupe" className="bg-gray-800">Coupe</option>
                  <option value="Minivan" className="bg-gray-800">Minivan</option>
                  <option value="Electric" className="bg-gray-800">Electric Vehicle</option>
                  <option value="Hybrid" className="bg-gray-800">Hybrid</option>
                  <option value="Other" className="bg-gray-800">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">What's your budget range? *</label>
                <select
                  name="budget"
                  required
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 cursor-pointer"
                >
                  <option value="" className="bg-gray-800">Select budget range</option>
                  <option value="Under $30,000" className="bg-gray-800">Under $30,000</option>
                  <option value="$30,000 - $40,000" className="bg-gray-800">$30,000 - $40,000</option>
                  <option value="$40,000 - $50,000" className="bg-gray-800">$40,000 - $50,000</option>
                  <option value="$50,000 - $60,000" className="bg-gray-800">$50,000 - $60,000</option>
                  <option value="$60,000 - $75,000" className="bg-gray-800">$60,000 - $75,000</option>
                  <option value="Over $75,000" className="bg-gray-800">Over $75,000</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">When do you need the vehicle? *</label>
                <select
                  name="timeline"
                  required
                  value={formData.timeline}
                  onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 cursor-pointer"
                >
                  <option value="" className="bg-gray-800">Select timeline</option>
                  <option value="ASAP (Within 1 week)" className="bg-gray-800">ASAP (Within 1 week)</option>
                  <option value="Within 2-4 weeks" className="bg-gray-800">Within 2-4 weeks</option>
                  <option value="Within 1-2 months" className="bg-gray-800">Within 1-2 months</option>
                  <option value="Within 3-6 months" className="bg-gray-800">Within 3-6 months</option>
                  <option value="Just researching" className="bg-gray-800">Just researching</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">How far along are you in the buying process? *</label>
                <select
                  name="buyingStage"
                  required
                  value={formData.buyingStage}
                  onChange={(e) => setFormData({ ...formData, buyingStage: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 cursor-pointer"
                >
                  <option value="" className="bg-gray-800">Select stage</option>
                  <option value="Just starting research" className="bg-gray-800">Just starting research</option>
                  <option value="Know what I want" className="bg-gray-800">Know what I want</option>
                  <option value="Ready to buy now" className="bg-gray-800">Ready to buy now</option>
                  <option value="Have financing approved" className="bg-gray-800">Have financing approved</option>
                  <option value="Need help with everything" className="bg-gray-800">Need help with everything</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Additional Details (Optional)</label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
                  placeholder="Any specific requirements or questions? (Max 500 characters)"
                />
                <div className="text-xs text-gray-400 mt-1">{formData.message.length}/500 characters</div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-cyan-500 to-teal-600 text-white py-4 rounded-xl text-lg font-bold hover:shadow-2xl transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting...' : 'Get My Free Consultation'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-500/20 border border-green-500 text-green-300 px-4 py-3 rounded-xl">
                  Thank you! We'll contact you within 24 hours with your personalized quote.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-3 rounded-xl">
                  Something went wrong. Please try again or call us directly.
                </div>
              )}

              <div className="text-center text-sm text-gray-400">
                🔒 Your information is secure and will never be shared
              </div>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-gradient-to-br from-cyan-600 to-teal-600 rounded-3xl p-8">
              <h3 className="text-3xl font-bold mb-6">Why Choose Us?</h3>
              <div className="space-y-4">
                {[
                  { icon: 'ri-time-line', text: 'Save 40+ hours of dealership visits' },
                  { icon: 'ri-money-dollar-circle-line', text: 'Save $3,500 on average' },
                  { icon: 'ri-shield-check-line', text: 'No obligation consultation' },
                  { icon: 'ri-customer-service-2-line', text: '24/7 support available' },
                  { icon: 'ri-car-line', text: 'Access to 50+ dealerships' },
                  { icon: 'ri-robot-line', text: 'AI-powered matching system' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <span className="text-lg">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-map-pin-line text-2xl text-cyan-400 mt-1"></i>
                  <div>
                    <div className="font-semibold">Location</div>
                    <div className="text-gray-300">Woodbridge, Ontario, Canada</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-time-line text-2xl text-cyan-400 mt-1"></i>
                  <div>
                    <div className="font-semibold">Hours</div>
                    <div className="text-gray-300">Mon-Fri: 9AM-8PM</div>
                    <div className="text-gray-300">Sat-Sun: 10AM-6PM</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-customer-service-2-line text-2xl text-cyan-400 mt-1"></i>
                  <div>
                    <div className="font-semibold">Response Time</div>
                    <div className="text-gray-300">Within 24 hours guaranteed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
